import { Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, forkJoin, from, Observable } from 'rxjs';
import { concatMap, first, map, shareReplay, switchMap, tap, toArray } from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { idMapToArray, ScopeService } from './scope.service';

export interface PerimeterDocumentTypes extends KPI.Perimeter {
  /** Map associating a `DocumentType` id. with a `DocumentTypePerimeterRequirement`. */
  document_types: { [id: number]: KPI.DocumentTypePerimeterRequirement };
  sub_perimeters_document_types: PerimeterDocumentTypes[];
}

export enum DocumentTypeRequirementState {
  NO,
  OPTIONAL,
  MANDATORY,
}

@Injectable()
export class DocumentTypesService {

  private _documentTypes$: Observable<KPI.DocumentType[]>;
  private _perimetersDocumentTypes$: Observable<PerimeterDocumentTypes[]>;
  private _documentTypeRequirementStates$: Observable<{ value: DocumentTypeRequirementState, label: string }[]>;
  private documentTypesRefresh$ = new BehaviorSubject<void>(null);

  constructor(
    private api: APIService,
    private scope: ScopeService,
    private translate: TranslateService,
  ) { }

  public get documentTypes$(): Observable<KPI.DocumentType[]> {
    if (!this._documentTypes$) {
      this._documentTypes$ = this.documentTypesRefresh$.pipe(
        switchMap(() => this.api.documents_document_types(null, true)),
        shareReplay(1),
      );
    }

    return this._documentTypes$;
  }

  public refreshDocumentTypes(): void {
    this.documentTypesRefresh$.next();
  }

  public get perimetersDocumentTypes$(): Observable<PerimeterDocumentTypes[]> {
    if (!this._perimetersDocumentTypes$) {
      this._perimetersDocumentTypes$ = combineLatest(
        this.documentTypes$,
        this.scope.perimeters$.pipe(idMapToArray()),
      ).pipe(
          map(([documentTypes, perimeters]) => {
            return perimeters.map(
              (perimeter: KPI.Perimeter) => this.getPerimeterDocumentTypes(perimeter, documentTypes),
            );
          }),
          shareReplay(1),
      );
    }

    return this._perimetersDocumentTypes$;
  }

  public get documentTypeRequirementStates$(): Observable<{ value: DocumentTypeRequirementState, label: string }[]> {
    if (!this._documentTypeRequirementStates$) {
      this._documentTypeRequirementStates$ = from(
        [
          {
            value: DocumentTypeRequirementState.NO,
            label: _('core.utils.document_types.requirement_no'),
          },
          {
            value: DocumentTypeRequirementState.OPTIONAL,
            label: _('core.utils.document_types.requirement_optional'),
          },
          {
            value: DocumentTypeRequirementState.MANDATORY,
            label: _('core.utils.document_types.requirement_mandatory'),
          },
        ],
      ).pipe(
        concatMap((item) =>
          this.translate.get(item.label).pipe(
            map((translatedLabel) => ({
              ...item,
              label: translatedLabel,
            })),
          ),
        ),
        toArray(),
        shareReplay(1),
      );
    }

    return this._documentTypeRequirementStates$;
  }

  public createDocumentType(label: string, order: number = 0): Observable<KPI.DocumentType> {
    return this.api.documents_document_types_post({
      label,
      order,
      perimeter_requirements: [],
      asset_type_requirements: [],
      investment_type_requirements: [],
      initiative_type_requirements: [],
    }).pipe(
      tap(() => this.refreshDocumentTypes()),
    );
  }

  public updateDocumentType(id: number, documentType: Partial<KPI.DocumentType>): Observable<KPI.DocumentType> {
    return this.api.documents_document_types_patch(id, documentType).pipe(
      tap(() => this.refreshDocumentTypes()),
    );
  }

  public updateDocumentTypesOrders(updates: { id: number; order: number }[]): Observable<void> {
    return forkJoin(
      updates.map(({ id, order }) => this.api.documents_document_types_patch(id, { order })),
    ).pipe(
      tap(() => this.refreshDocumentTypes()),
      map(() => void 0),
    );
  }

  public deleteDocumentType(id: number): Observable<void> {
    return this.api.documents_document_types_delete(id).pipe(
      tap(() => this.refreshDocumentTypes()),
    );
  }

  public updateDocumentTypePerimeterRequirement(
    documentTypeId: number,
    perimeter: number,
    state: DocumentTypeRequirementState,
  ): Observable<KPI.DocumentType> {
    return this.documentTypes$.pipe(
      first(),
      map((documentTypes) => documentTypes.find((d) => d.id === documentTypeId)),
      concatMap((documentType) => {
        // Remove requirement
        if (state === DocumentTypeRequirementState.NO) {
          documentType.perimeter_requirements = documentType.perimeter_requirements.filter(
            (r) => r.perimeter !== perimeter,
          );
        // Add or update requirement
        } else {
          const mandatory = state === DocumentTypeRequirementState.MANDATORY;
          const perimeterRequirementIndex =
            documentType.perimeter_requirements.findIndex((r) => r.perimeter === perimeter);
          if (perimeterRequirementIndex < 0) {
            documentType.perimeter_requirements.push({
              perimeter,
              mandatory,
            });
          } else {
            documentType.perimeter_requirements[perimeterRequirementIndex].mandatory = mandatory;
          }
        }

        return this.updateDocumentType(
          documentTypeId,
          {
            perimeter_requirements: documentType.perimeter_requirements,
          },
        );
      }),
    );
  }

  private getPerimeterDocumentTypes(
    perimeter: KPI.Perimeter,
    documentTypes: KPI.DocumentType[],
  ): PerimeterDocumentTypes {
    return {
      ...perimeter,
      document_types: documentTypes.reduce(
        (perimeterDocumentTypes, documentType) => {
          const perimeterRequirement = documentType.perimeter_requirements.find(
            (r) => r.perimeter === perimeter.id,
          );

          const requirementValue = perimeterRequirement ?
            (
              perimeterRequirement.mandatory ?
              DocumentTypeRequirementState.MANDATORY :
              DocumentTypeRequirementState.OPTIONAL
            ) :
            DocumentTypeRequirementState.NO
          ;

          return {
            ...perimeterDocumentTypes,
            [documentType.id]: requirementValue,
          };
        },
        {},
      ),
      sub_perimeters_document_types:
        perimeter.sub_perimeters &&
        perimeter.sub_perimeters.map((subPerimeter) => this.getPerimeterDocumentTypes(subPerimeter, documentTypes)) ||
        []
      ,
    };
  }

  public getDefaultDocumentTypesForInvestment(investmentId: number): Observable<KPI.DocumentRequirement[]> {
    return this.api.documents_default_investment_document_types(investmentId).pipe(
      tap(() => this.refreshDocumentTypes()),
    );
  }

  public getAllDocumentTypes(): Observable<KPI.DocumentType[]> {
    return this.api.documents_document_types(null, true);
  }

  public getDefaultDocumentTypesForAsset(assetId: number): Observable<KPI.DocumentRequirement[]> {
    return this.api.documents_default_asset_document_types(assetId);
  }

  public getDefaultDocumentTypesForInitiative(initiativeId: number): Observable<KPI.DocumentRequirement[]> {
    return this.api.documents_default_initiative_document_types(initiativeId);
  }

  public getDocumentTypesForPerimeter(perimeterId: number): Observable<KPI.DocumentType[]> {
    return forkJoin([
      this.documentTypes$.pipe(first()),
      this.api.documents_document_type_relationships({ object_id: perimeterId }, true),
    ]).pipe(
      map(([allTypes, newRelationships]) => {
        const oldTypeIds = new Set(
          allTypes
            .filter(dt => dt.perimeter_requirements?.some(req => req.perimeter === perimeterId))
            .map(dt => dt.id)
        );
        const newTypeIds = new Set(newRelationships.map(rel => rel.document_type));
        const mergedIds = new Set([...oldTypeIds, ...newTypeIds]);
        return allTypes.filter(dt => mergedIds.has(dt.id));
      }),
    );
  }
}
